// /admin/usuarios data seam — the INHERTA staff console: team roster, pending
// invites, member row actions (role change, deactivate/reactivate, password
// reset, MFA reset, session revoke), the direct-create flow, the team-wide
// MFA-required setting, and the global user directory (Directorio tab).
//
// Wired: getOrgUsers() -> GET /admin/users (members + pending invites),
// cancelOrgInvite() -> DELETE /admin/users/invites/:id,
// resendOrgInvite() -> POST /admin/users/invites/:id/resend,
// changeRole() -> PATCH /admin/users/:id/role,
// deactivateUser() -> POST /admin/users/:id/deactivate,
// reactivateUser() -> POST /admin/users/:id/reactivate,
// sendPasswordReset() -> POST /admin/users/:id/password-reset,
// resetMfa() -> POST /admin/users/:id/mfa/reset,
// revokeUserSessions() -> POST /admin/users/:id/sessions/revoke,
// createUser() -> POST /admin/users (direct-create, returns a temp password),
// getSecuritySettings()/setMfaRequired() -> GET/PATCH /admin/settings/security,
// searchDirectory() -> GET /admin/directory (every user, staff + client, with
// their tenant memberships — cross-tenant support lookup).
// grantableRoles()/inviteTeamMember() live in account-managers-data.ts (the
// invite picker is shared with the /admin/account-managers card view) and are
// re-exported here so this page has one seam to import from.

import { PLATFORM_ROLES } from './account-managers-data.js'
import { apiClient } from '../../api/client.js'

export { grantableRoles, inviteTeamMember, PLATFORM_ROLES } from './account-managers-data.js'

export type PlatformRoleValue = (typeof PLATFORM_ROLES)[number]['value']

export interface OrgMember {
  id: string
  email: string
  name: string
  platform_role: PlatformRoleValue
  active: boolean
  mfa_enabled: boolean
  last_login: string | null
  has_avatar: boolean
  secondary_email: string | null
}

export interface UserTenantMembership {
  id: string
  name: string
  role: string
}

export interface UserDetail {
  id: string
  email: string
  name: string
  /** null for a tenant-only user — the details page serves ANY user, not
   *  just INHERTA staff (mirrors GET /admin/directory's scope). */
  platform_role: PlatformRoleValue | null
  active: boolean
  mfa_enabled: boolean
  mfa_enrolled_at: string | null
  last_login: string | null
  created_at: string
  has_avatar: boolean
  /** Their tenant memberships — `[]` for pure staff. */
  tenants: UserTenantMembership[]
  /** Present ONLY when the caller may manage it (server-decided rail —
   *  owner always, admin only for a non-elevated target — a tenant user is
   *  never elevated); absent entirely otherwise. When present it may still
   *  be `null` (visible, just unset). */
  secondary_email?: string | null
  /** Present under the same rail as secondary_email (and only when the
   *  address itself is set — the pairing invariant, mirrored client-side).
   *  'pending' until the verify link is clicked, 'verified' after. */
  secondary_email_status?: 'pending' | 'verified' | null
}

export interface UserSession {
  id: string
  device: string
  kind: 'desktop' | 'mobile'
  location: string
  lastActive: string
}

export interface PendingInvite {
  id: string
  email: string
  platform_role: PlatformRoleValue
  invited_by: string | null
  created_at: string
  expires_at: string
}

export interface OrgUsersData {
  members: OrgMember[]
  /** Total members matching the search (ignores limit/offset) — for the
   *  Equipo tab's "Anterior"/"Siguiente" bounds, not the search itself. */
  members_total: number
  pending: PendingInvite[]
}

/** Same labels PLATFORM_ROLES carries, keyed for direct lookup. */
export const PLATFORM_ROLE_LABEL: Record<PlatformRoleValue, string> = Object.fromEntries(
  PLATFORM_ROLES.map((r) => [r.value, r.label]),
) as Record<PlatformRoleValue, string>

export const ROLE_BADGE: Record<
  PlatformRoleValue,
  { tone: 'info' | 'ok' | 'warn' | 'draft'; label: string }
> = {
  platform_owner: { tone: 'info', label: PLATFORM_ROLE_LABEL.platform_owner },
  platform_admin: { tone: 'ok', label: PLATFORM_ROLE_LABEL.platform_admin },
  platform_operator: { tone: 'draft', label: PLATFORM_ROLE_LABEL.platform_operator },
  platform_account_manager: { tone: 'warn', label: PLATFORM_ROLE_LABEL.platform_account_manager },
}

/** GET /admin/users — the INHERTA team roster (searchable, server-paginated
 *  via q/limit/offset) + pending invites (always the full unpaginated list —
 *  the server ignores q/limit/offset for that half of the response). Empty
 *  `q` returns every member. */
export async function getOrgUsers(q = '', limit = 25, offset = 0): Promise<OrgUsersData> {
  const params = new URLSearchParams({ q, limit: String(limit), offset: String(offset) })
  const res = await apiClient.get<{ data: OrgUsersData }>(`/admin/users?${params}`)
  return res.data.data
}

/** DELETE /admin/users/invites/:id — withdraws a not-yet-accepted invite. */
export async function cancelOrgInvite(id: string): Promise<void> {
  await apiClient.delete(`/admin/users/invites/${encodeURIComponent(id)}`)
}

/** POST /admin/users/invites/:id/resend — re-sends the invite email (fresh expiry). */
export async function resendOrgInvite(id: string): Promise<void> {
  await apiClient.post(`/admin/users/invites/${encodeURIComponent(id)}/resend`, {})
}

/** PATCH /admin/users/:id/role — changes a member's platform role. The API
 *  revokes their active sessions on success (a role change takes effect on
 *  next login, not mid-session). */
export async function changeRole(id: string, platformRole: string): Promise<void> {
  await apiClient.patch(`/admin/users/${encodeURIComponent(id)}/role`, {
    platform_role: platformRole,
  })
}

/** POST /admin/users/:id/deactivate — blocks login and closes active sessions. */
export async function deactivateUser(id: string, reason: string): Promise<void> {
  await apiClient.post(`/admin/users/${encodeURIComponent(id)}/deactivate`, { reason })
}

/** POST /admin/users/:id/reactivate — restores login access. */
export async function reactivateUser(id: string): Promise<void> {
  await apiClient.post(`/admin/users/${encodeURIComponent(id)}/reactivate`, {})
}

/** DELETE /admin/users/:id — hard-deletes a DEACTIVATED account (owner-only).
 *  `confirm` must equal the account's exact email. */
export async function deleteUser(id: string, confirm: string): Promise<void> {
  await apiClient.delete(`/admin/users/${encodeURIComponent(id)}`, { data: { confirm } })
}

/** PATCH /admin/users/:id/secondary-email — sets (or clears, with null) the
 *  notification-only secondary email. */
export async function setSecondaryEmail(id: string, value: string | null): Promise<void> {
  await apiClient.patch(`/admin/users/${encodeURIComponent(id)}/secondary-email`, {
    secondary_email: value,
  })
}

/** GET /admin/users/:id — the user-details page. `secondary_email` is present
 *  on the response only when the server decided the caller may see it. */
export async function getUserDetail(id: string): Promise<UserDetail> {
  const res = await apiClient.get<{ data: UserDetail }>(`/admin/users/${encodeURIComponent(id)}`)
  return res.data.data
}

/** GET /admin/users/:id/sessions — the target's active sessions, formatted
 *  server-side (same device/location/lastActive shape as the account Sesiones
 *  panel). */
export async function adminListUserSessions(id: string): Promise<UserSession[]> {
  const res = await apiClient.get<{ data: UserSession[] }>(
    `/admin/users/${encodeURIComponent(id)}/sessions`,
  )
  return res.data.data
}

/** POST /admin/users/:id/password-reset — emails a password-reset link. */
export async function sendPasswordReset(id: string): Promise<void> {
  await apiClient.post(`/admin/users/${encodeURIComponent(id)}/password-reset`, {})
}

/** POST /admin/users/:id/mfa/reset — clears their TOTP enrollment; they
 *  re-enroll on next login. Closes their active sessions. */
export async function resetMfa(id: string, reason: string): Promise<void> {
  await apiClient.post(`/admin/users/${encodeURIComponent(id)}/mfa/reset`, { reason })
}

/** POST /admin/users/:id/sessions/revoke — closes every active session for
 *  this member; resolves to how many were revoked. */
export async function revokeUserSessions(id: string): Promise<{ revoked: number }> {
  const res = await apiClient.post<{ data: { revoked: number } }>(
    `/admin/users/${encodeURIComponent(id)}/sessions/revoke`,
    {},
  )
  return res.data.data
}

/** Either the temp password is shown on screen, or it was emailed to the
 *  account's own address or its secondary — never both. */
export type CreatedUser =
  | { user_id: string; temp_password: string }
  | { user_id: string; delivered_to: 'primary_email' | 'secondary_email' }

export interface CreateUserOptions {
  secondaryEmail?: string | undefined
  /** 'screen' (default): show the temp password once, here. 'primary_email':
   *  email it to the new account's own address. 'secondary_email': email it to
   *  secondaryEmail instead — requires secondaryEmail to be set. */
  deliverCredentials?: 'screen' | 'primary_email' | 'secondary_email' | undefined
}

/** POST /admin/users — direct-creates an account (no invite email). By
 *  default returns a one-time temp password the operator must relay
 *  themselves; with `deliverCredentials: 'secondary_email'` the API emails it
 *  instead and the response carries no password. */
export async function createUser(
  email: string,
  name: string,
  platformRole: string,
  options?: CreateUserOptions,
): Promise<CreatedUser> {
  const res = await apiClient.post<{ data: CreatedUser }>('/admin/users', {
    email,
    name,
    platform_role: platformRole,
    ...(options?.secondaryEmail ? { secondary_email: options.secondaryEmail } : {}),
    ...(options?.deliverCredentials ? { deliver_credentials: options.deliverCredentials } : {}),
  })
  return res.data.data
}

export interface UnenrolledStaff {
  id: string
  name: string
  email: string
}

export interface SecuritySettings {
  mfa_required: boolean
  unenrolled_staff: UnenrolledStaff[]
}

/** GET /admin/settings/security — the team-wide MFA-required setting plus who
 *  would be locked out if it were turned on right now. */
export async function getSecuritySettings(): Promise<SecuritySettings> {
  const res = await apiClient.get<{ data: SecuritySettings }>('/admin/settings/security')
  return res.data.data
}

/** PATCH /admin/settings/security — flips the team-wide MFA-required setting. */
export async function setMfaRequired(value: boolean): Promise<void> {
  await apiClient.patch('/admin/settings/security', { mfa_required: value })
}

export interface DirectoryTenantMembership {
  tenant_id: string
  tenant_name: string
  role: string
}

export interface DirectoryResult {
  id: string
  email: string
  name: string
  platform_role: PlatformRoleValue | null
  active: boolean
  tenants: DirectoryTenantMembership[]
  has_avatar: boolean
}

export interface DirectorySearch {
  results: DirectoryResult[]
  limit: number
  offset: number
}

/** Tenant-membership roles a directory row can carry, with their console
 *  labels. Declared here rather than imported from @vysto/shared: the web must
 *  not pull the shared barrel (it drags jsonwebtoken/pino into the bundle).
 *  The API validates the value against the real catalog — this list only
 *  decides what the picker offers. */
export const TENANT_ROLES = [
  { value: 'tenant_owner', label: 'Dueño' },
  { value: 'tenant_admin', label: 'Administrador' },
  { value: 'tenant_agent', label: 'Asesor' },
  { value: 'tenant_viewer', label: 'Solo lectura' },
] as const

/** The Directorio tab's optional narrowing. Every field composes (AND) with
 *  `q` and with the others; `''` / undefined means "no filter". */
export interface DirectoryFilters {
  tenantId?: string | undefined
  platformRole?: string | undefined
  tenantRole?: string | undefined
  /** true = only active accounts, false = only deactivated, undefined = both. */
  active?: boolean | undefined
}

/** GET /admin/directory — every user on the platform (INHERTA staff and
 *  client users alike), searchable by email/name fragment and narrowable by
 *  tenant, role and account state, with their tenant memberships. Empty `q`
 *  and no filters returns every user, paginated. */
export async function searchDirectory(
  q: string,
  limit = 25,
  offset = 0,
  filters: DirectoryFilters = {},
): Promise<DirectorySearch> {
  const params = new URLSearchParams({
    q,
    limit: String(limit),
    offset: String(offset),
  })
  // Only send a param that is actually set — an empty `tenant_id=` would fail
  // the server's uuid check and 400 the whole search.
  if (filters.tenantId) params.set('tenant_id', filters.tenantId)
  if (filters.platformRole) params.set('platform_role', filters.platformRole)
  if (filters.tenantRole) params.set('tenant_role', filters.tenantRole)
  if (filters.active !== undefined) params.set('active', String(filters.active))
  const res = await apiClient.get<{ data: DirectorySearch }>(`/admin/directory?${params}`)
  return res.data.data
}

/** Short es-MX date for a table cell ("Último acceso", "Vence"); `null` reads
 *  as "Nunca" (last_login only — expires_at is always set). Adds the year
 *  when it isn't the current one. */
export function relativeDateEs(iso: string | null): string {
  if (iso === null) return 'Nunca'
  const date = new Date(iso)
  const sameYear = date.getFullYear() === new Date().getFullYear()
  return new Intl.DateTimeFormat('es-MX', {
    day: 'numeric',
    month: 'short',
    ...(sameYear ? {} : { year: 'numeric' }),
  }).format(date)
}

/** Full es-MX date + time for the user-details page ("último acceso",
 *  "miembro desde") — the table's relativeDateEs() stays the short form;
 *  this is its absolute counterpart. `null` reads as 'Nunca'. */
export function absoluteDateTimeEs(iso: string | null): string {
  if (iso === null) return 'Nunca'
  return new Intl.DateTimeFormat('es-MX', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(iso))
}

/** "MFA enrolada" row on the user-details page. `null` there is never really
 *  "nunca" (that copy reads as an event that failed to happen, and for a
 *  person with no MFA at all it's simply not applicable): with MFA off it's
 *  'Sin configurar'; with MFA on but no stored enrollment date (legacy rows
 *  predating the column, or backfilled accounts) it's 'Activo'; otherwise the
 *  actual enrollment date/time. */
export function mfaEnrolledLabel(mfaEnabled: boolean, mfaEnrolledAt: string | null): string {
  if (!mfaEnabled) return 'Sin configurar'
  if (mfaEnrolledAt === null) return 'Activo'
  return absoluteDateTimeEs(mfaEnrolledAt)
}
